import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  startEventPromoUploadQueue,
  subscribeEventPromoUploadQueue,
} from "../../utils/eventPromoUploadQueue";

const EMPTY_STATE = { jobs: [], total: 0, uploading: 0, failed: 0, completed: 0, percent: 0 };

export default function EventPromoUploadStatus({ userId }) {
  const { t } = useTranslation("events");
  const [state, setState] = useState(EMPTY_STATE);
  const notifiedOutcomesRef = useRef(new Set());

  useEffect(() => {
    if (!userId) return undefined;
    const unsubscribe = subscribeEventPromoUploadQueue(setState);
    startEventPromoUploadQueue(userId);
    return unsubscribe;
  }, [userId]);

  useEffect(() => {
    if (!userId || !state.total || state.uploading > 0) return;

    const terminalJobs = state.jobs.filter(
      (job) => job.status === "complete" || (job.status === "failed" && Number(job.attempts || 0) >= 3)
    );
    if (terminalJobs.length !== state.total) return;

    const outcome = terminalJobs.some((job) => job.status === "failed") ? "failed" : "complete";
    const notificationKey = `${outcome}:${terminalJobs
      .map((job) => job.id)
      .sort()
      .join("|")}`;
    if (notifiedOutcomesRef.current.has(notificationKey)) return;
    notifiedOutcomesRef.current.add(notificationKey);

    window.dispatchEvent(
      new CustomEvent(outcome === "complete" ? "global-info" : "global-error", {
        detail: {
          message: t(`promo_background_upload.${outcome}`),
        },
      })
    );
  }, [state, t, userId]);

  if (!userId || state.uploading <= 0) return null;

  const activeJobs = state.jobs.filter((job) =>
    ["persisting", "queued", "retrying", "uploading"].includes(job.status)
  );
  const activeTotalBytes = activeJobs.reduce(
    (sum, job) => sum + Math.max(0, Number(job.total || 0)),
    0
  );
  const activeLoadedBytes = activeJobs.reduce(
    (sum, job) => sum + Math.max(0, Number(job.loaded || 0)),
    0
  );
  const activePercent = activeTotalBytes > 0
    ? Math.min(100, Math.round((activeLoadedBytes / activeTotalBytes) * 100))
    : Math.max(0, Math.min(100, Number(state.percent || 0)));

  return (
    <div
      className="pointer-events-none fixed inset-x-0 z-[91] flex justify-center lg:bottom-6"
      data-bottom-clearance-overlay
      data-bottom-clearance-gap="8"
      style={{
        bottom: "calc(max(env(safe-area-inset-bottom, 0px), 10px) + var(--bottom-nav-height, 72px) + 8px)",
      }}
      role="status"
      aria-live="polite"
    >
      <div
        data-testid="event-media-upload-progress"
        className="flex h-[70px] w-[var(--vg-bottom-nav-width,min(calc(100vw-48px),430px))] items-center gap-3 overflow-hidden rounded-full px-4 text-white"
        style={{
          backgroundColor: "rgba(53, 53, 53, 0.5)",
          backdropFilter: "blur(15px)",
          WebkitBackdropFilter: "blur(15px)",
          boxShadow:
            "inset 0 0 0 0.5px rgba(255,255,255,0.24), inset 0 0 0 1.5px rgba(255,255,255,0.08)",
        }}
      >
        <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-full bg-white/10 text-[14px] font-bold">
          {activePercent}%
        </div>
        <div className="min-w-0 flex-1 pr-1">
          <p className="truncate text-[14px] font-semibold leading-5">
            {t("promo_background_upload.uploading", { count: activeJobs.length || state.uploading })}
          </p>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full bg-[#5B91FF] transition-[width] duration-200"
              style={{ width: `${activePercent}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
